import { isNamedForm } from "@/constants/medication-dosage";
import { VitallExtensionUrl } from "@/constants/titration";
import { extractMedNameFromMedStatement } from "@/utils";
import {
  CodeableConceptInput,
  DiscontinuedTitrationMedicationInput,
  DosageInput,
  GdmtMedicationClass,
  MedicationIngredientInput,
  TitrationDrugClass,
  TitrationMedicationInput,
  TitrationStepInput,
} from "@/utils/GraphQLClient/titration";
import { DosageStep, dosageToSteps, emptyDosageStep, stepsToDosage } from "@/utils/dosage-steps";
import {
  buildStrengthDenominator,
  buildStrengthQuantity,
  formValueFromLabel,
  getContainedMedication,
  medicationToIngredientRows,
} from "@/utils/medication-ingredients";

import { fhirR4 } from "@smile-cdr/fhirts";

import {
  CUSTOM_MEDICATION_ID,
  Dosage,
  DosageValue,
  TitrationPlanEntry,
  dosageValueHasContent,
} from "./titration-plan-types";

/**
 * FE `GdmtMedicationClass` keys (`rasi` / `sglt2i` / `nsmra` / `betaBlocker`)
 * and BE `TitrationDrugClass` enum values (`RASi` / `SGLT2i` / `nsMRA` /
 * `BetaBlocker`) intentionally diverge. The FE casing matches Figma + the
 * existing `MEDICATION_CLASS_MAP`; the BE casing matches the Engage Vitall
 * codesystem display. This map bridges them at the API boundary.
 */
const FE_TO_BE_DRUG_CLASS: Record<GdmtMedicationClass, TitrationDrugClass> = {
  [GdmtMedicationClass.Rasi]: TitrationDrugClass.RASi,
  [GdmtMedicationClass.Sglt2i]: TitrationDrugClass.SGLT2i,
  [GdmtMedicationClass.Mra]: TitrationDrugClass.nsMRA,
  [GdmtMedicationClass.BetaBlocker]: TitrationDrugClass.BetaBlocker,
};

/**
 * Project a FHIR `MedicationStatement` into a partial `TitrationPlanEntry`
 * for the auto-fill flow. The caller spreads the result into the entry via
 * the `onChange` callback. `medicationId` is set to the MS id so the BE
 * receives it as `source_medication_statement_id` on save.
 *
 * Reads `dosage[0]` only — the form is single-step per class (no taper
 * ladders, per VMD-587 scope). Since the entry fields now share the
 * `DosageStep` vocabulary, this is a near-identity copy with no
 * label/code translation.
 */
export const medicationStatementToAutofillPatch = (ms: fhirR4.MedicationStatement): Partial<TitrationPlanEntry> => {
  const step = dosageToSteps(ms.dosage)[0] ?? emptyDosageStep();
  // Custom-frequency triple + timing now live on each `Dosage`. Source:
  // `Timing.repeat.periodUnit` / `Timing.repeat.when` — already constrained to
  // the same FHIR vocabularies as the FE enums, so these are narrowing casts at
  // the API boundary rather than runtime coercions.
  const frequencyFields = {
    frequencyTimes: step.frequencyTimes,
    frequencyEvery: step.frequencyEvery,
    frequencyUnit: step.frequencyUnit as Dosage["frequencyUnit"],
    timing: step.timing as Dosage["timing"],
  };
  // The current dose is the *strength* ("25 mg"), which lives on
  // `Medication.ingredient[].strength.numerator` — never the `doseQuantity`,
  // which carries the counted amount taken ("2 tablets"). "Strength is not
  // quantity" (MEDICATION_TRANSFORM_RULES.md): the count + its noun fill
  // `quantity` / `form`, and only the strength drives the dose fields.
  const strength = statementStrength(ms);
  // The dose form is the counted noun ("tablet"). The quantity's unit is the
  // form, so `step.doseUnit` (the `doseQuantity` unit) is authoritative here,
  // falling back to the contained Medication's `form` — matching the precedence
  // in `medicationStatementToFormData`. `formValueFromLabel` maps a stored label
  // back to its option value.
  const form = step.doseUnit || formValueFromLabel(getContainedMedication(ms)?.form?.text);
  return {
    medicationId: ms.id ?? "",
    medicationName: extractMedNameFromMedStatement(ms),
    form,
    // The counted amount taken ("2 tablets"): the `doseQuantity` value. Absent
    // where the record never stated a count.
    quantity: step.doseValue.trim() !== "" ? Number(step.doseValue) : undefined,
    currentDosage: {
      value: strength.value,
      unit: strength.unit,
      frequency: step.frequencyPreset,
      ...frequencyFields,
    },
    // The frequency / timing form controls bind to `newDosage` (the target
    // dosage mirrors them), so the source med's schedule has to land there too or
    // the dropdowns render empty after a pick. The value stays empty — there is
    // no "new dose" field in the add-medication flow, so the strength falls back
    // to `currentDosage` on save (see `entryToDosageStep`).
    newDosage: {
      value: {},
      unit: strength.unit,
      frequency: step.frequencyPreset,
      ...frequencyFields,
    },
    // Autofill target dose's unit & frequency - omit value for user to input
    targetDosage: {
      value: {},
      unit: strength.unit,
      frequency: step.frequencyPreset,
      ...frequencyFields,
    },
    route: ms.dosage?.[0]?.route?.text ?? "",
  };
};

/**
 * The medication's strength as a `Dosage`-shaped value + unit, read off the
 * contained Medication's `ingredient[].strength.numerator`. A combination
 * product ("24/26 mg") carries one strength per active ingredient, so the first
 * two populated strengths become the low / high bounds; a single-ingredient
 * product populates `low` only. With no stated strength the value is empty and
 * the unit defaults to "mg" (matching the empty-dose default) so the unit picker
 * has a valid selection.
 */
const statementStrength = (ms: fhirR4.MedicationStatement): { value: DosageValue; unit: string } => {
  const rows = medicationToIngredientRows(ms).filter(row => row.strength.trim() !== "");
  const value: DosageValue = {
    low: rows[0] ? Number(rows[0].strength) : undefined,
    high: rows[1] ? Number(rows[1].strength) : undefined,
  };
  return { value, unit: rows[0]?.unit || "mg" };
};

/** Bridge a `DosageStep`'s low/high string fields into a `DosageValue` object. */
const dosageStepToValue = (step: DosageStep): DosageValue => ({
  low: step.doseValue.trim() !== "" ? Number(step.doseValue) : undefined,
  high: step.doseValueHigh.trim() !== "" ? Number(step.doseValueHigh) : undefined,
});

/**
 * Build the BE `ingredient[]` for a titration med. Each ingredient carries the
 * product name (VMD-713: there is no ingredient-level breakdown) and its
 * strength as a Ratio — "25 mg per 1 tablet", where the numerator is the dose
 * strength and the denominator is the chosen Form. With no strength entered the
 * Ratio is omitted so the BE's min-1 element still holds the named product.
 *
 * A combination product ("20/15 mg", stored as `{ low: 20, high: 15 }`) states
 * one strength per active ingredient, so each populated bound becomes its own
 * `ingredient[]` entry — both named after the product and quoted against the
 * same Form.
 *
 * Mirrors `buildContainedMedication` in medication-ingredients.ts: the Form is
 * the denominator every strength is quoted against, and "Unknown" names no form,
 * so the denominator falls back to a unitless `{ value: 1 }` (satisfies rat-1).
 */
const buildIngredientInput = (
  concept: CodeableConceptInput,
  strength: DosageValue,
  strengthUnit: string,
  form: string
): MedicationIngredientInput[] => {
  // Every ingredient is named after the product (VMD-713). Carry the whole
  // medication concept — its `coding` as well as `text` — so the ingredient is
  // never left unnamed when the name lives on `coding.display` (an EMR-sourced
  // concept can carry no `text` at all).
  const itemCodeableConcept: CodeableConceptInput = { ...concept, text: concept.text?.trim() ?? "" };
  const values = [strength.low, strength.high].filter((value): value is number => value !== undefined);

  // No strength stated (or no unit): a single named ingredient with no Ratio
  // still satisfies the BE's required min-1 element.
  if (!strengthUnit || values.length === 0) {
    return [{ itemCodeableConcept, isActive: true }];
  }

  const namedForm = isNamedForm(form) ? form : "";
  const denominator = buildStrengthDenominator(1, namedForm);
  return values.map(value => ({
    itemCodeableConcept,
    isActive: true,
    strength: {
      numerator: buildStrengthQuantity(value, strengthUnit),
      denominator,
    },
  }));
};

/**
 * Build a step's `form` CodeableConcept. Mirrors `buildContainedMedication`:
 * "Unknown" (and any non-named form) is an admitted absence, not a dose form, so
 * it names no form and the text is left empty rather than asserting one the
 * source never stated. The same Form quotes both the strength denominator (via
 * `buildIngredientInput`) and this concept.
 */
const buildFormConcept = (form: string): CodeableConceptInput => ({
  text: isNamedForm(form) ? form : "",
});

const entryToDosageStep = (entry: TitrationPlanEntry): DosageStep => {
  const dosage = dosageValueHasContent(entry.newDosage.value) ? entry.newDosage : entry.currentDosage;
  // "as_needed" predates PRN becoming an additive flag rather than its own preset
  // (VMD-702) — a legacy value still needs to project onto `asNeeded`, not a preset.
  const isAsNeeded = dosage.frequency === "as_needed";
  return {
    ...emptyDosageStep(),
    // `dosage[].doseQuantity` is the *counted* dose ("2 tablets"): `quantity` is
    // the amount and `form` its noun. The strength ("25 mg") is a subjective
    // amount that belongs on `ingredient[].strength`, not here — see
    // MEDICATION_TRANSFORM_RULES.md ("Strength is not quantity").
    doseValue: entry.quantity?.toString() ?? "",
    doseUnit: entry.form,
    frequencyPreset: isAsNeeded ? "" : dosage.frequency,
    frequencyTimes: dosage.frequencyTimes,
    frequencyEvery: dosage.frequencyEvery,
    frequencyUnit: dosage.frequencyUnit,
    timing: dosage.timing,
    asNeeded: isAsNeeded,
  };
};

/**
 * Pull the titration target dose off a discontinued MedicationRequest. It lives
 * on the first dosageInstruction as a `MedicationTargetDose` extension whose
 * `valueDosage` is a FHIR Dosage — structurally a subset of the BE
 * `DosageInput`, so the cast is safe at the API boundary.
 */
const extractMedicationRequestTargetDose = (medRequest?: fhirR4.MedicationRequest): DosageInput | undefined => {
  const [dosageInstruction] = medRequest?.dosageInstruction ?? [];
  const targetValueDosage = (dosageInstruction?.extension ?? []).find(
    ({ url }) => url === VitallExtensionUrl.MedicationTargetDose
  )?.valueDosage;
  // When it comes from a GraphQL response, `valueDosage` and its nested
  // Quantity/Range objects carry `__typename` fields that the BE rejects, so
  // strip them before the cast at the API boundary. Null fields (e.g. the
  // unused doseQuantity/doseRange) are dropped too, since the BE rejects them.
  return targetValueDosage ? (stripTypename(targetValueDosage) as DosageInput) : undefined;
};

/**
 * Deep-clone a value, removing every `__typename` key GraphQL injects and any
 * field whose value is `null`.
 */
const stripTypename = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map(stripTypename) as T;
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key, val]) => key !== "__typename" && val !== null)
        .map(([key, val]) => [key, stripTypename(val)])
    ) as T;
  }
  return value;
};

/**
 * Project a FHIR `CodeableConcept` into the BE `CodeableConceptInput` shape,
 * keeping only `text` / `coding`. When the concept comes from a GraphQL
 * response it carries `__typename` fields that the BE rejects, so we rebuild a
 * clean object rather than spreading the source.
 */
const toCodeableConceptInput = (concept?: fhirR4.CodeableConcept): CodeableConceptInput => ({
  text: concept?.text ?? "",
  ...(concept?.coding && {
    coding: concept.coding.map(({ system, code, display }) => ({
      ...(system && { system }),
      ...(code && { code }),
      ...(display && { display }),
    })),
  }),
});

/** True when a concept actually names the med — via its `text` or a `coding.display`. */
const conceptNamesMedication = (concept: CodeableConceptInput): boolean =>
  !!concept.text?.trim() || !!concept.coding?.some(({ display }) => display?.trim());

/**
 * Resolve the concept naming a fetched MedicationRequest's medication. The
 * titration query never selects `medicationCodeableConcept`, so the coded
 * concept lives on the contained Medication's first ingredient
 * (`itemCodeableConcept`) — preferred here so its `coding` is preserved, with
 * `medicationReference.display` as a text-only fallback. Contained-first matches
 * `resolveMedicationName`.
 *
 * The contained concept only wins when it carries a usable name: an
 * `itemCodeableConcept` can be present but empty (`{ text: "" }`), which is
 * truthy yet names nothing, so it would otherwise emit an unnamed ingredient
 * (and top-level `medication_codeable_concept`). In that case fall back to the
 * denormalized `medicationReference.display`.
 */
const resolveMedicationRequestConcept = (medRequest?: fhirR4.MedicationRequest): CodeableConceptInput => {
  const itemCodeableConcept = medRequest && getContainedMedication(medRequest)?.ingredient?.[0]?.itemCodeableConcept;
  const concept = itemCodeableConcept ? toCodeableConceptInput(itemCodeableConcept) : undefined;
  if (concept && conceptNamesMedication(concept)) {
    return concept;
  }
  return { text: medRequest?.medicationReference?.display ?? "" };
};

/**
 * Project a FHIR `MedicationStatement` (the patient's current medication for a
 * class) into the `discontinued_medication` payload sent when a titration med
 * is being replaced. Shares the `DosageStep` roundtrip with the autofill path
 * so dose / frequency / route / timing map 1:1.
 *
 * The medication concept + dosage come from the statement; the target dose is
 * read off the discontinued `MedicationRequest`; and the titration metadata
 * (`decision`, `decision_reasons`, `reason_for_titration`, and
 * `source_medication_statement_id`) comes from the entry's `replacedMedication`
 * so the BE records why the med was discontinued and which record it links to.
 */
export const medicationRequestToDiscontinuedInput = (
  medClass: GdmtMedicationClass,
  medRequest: fhirR4.MedicationRequest,
  replacedMedication?: TitrationPlanEntry["replacedMedication"]
): DiscontinuedTitrationMedicationInput => {
  const step = dosageToSteps(medRequest?.dosageInstruction)[0] ?? emptyDosageStep();
  // stepsToDosage emits fhirR4.Dosage[]; the BE DosageInput shape is a
  // structural subset, so the cast is safe at the API boundary.
  const dosage = stripTypename(
    stepsToDosage([step], {
      route: medRequest.dosageInstruction?.[0].route?.text || undefined,
    }) as DosageInput[]
  );
  const targetDose = extractMedicationRequestTargetDose(medRequest);
  // A fetched MedicationRequest names its medication on the contained Medication's
  // ingredient concept (with `medicationReference.display` as a text-only fallback)
  // — never on `medicationCodeableConcept`, which the titration query does not
  // select. Sourcing the concept here keeps its `coding` on the discontinued payload.
  const medicationConcept = resolveMedicationRequestConcept(medRequest);
  // The discontinued med's dose form lives on its contained Medication (the
  // titration query selects `contained.form.text`). Read it back to its option
  // value the same contained-first way the MedicationStatement path does
  // (`resolveMedicationName` / `medicationToIngredientRows`). An empty or
  // unmatched form stays "", so the strength denominator falls back to a unitless
  // `{ value: 1 }` and `Medication.form` is left unnamed.
  const medicationForm = formValueFromLabel(getContainedMedication(medRequest)?.form?.text);
  // The strength Ratio is read off the dose step (value + unit, both bounds for a
  // combination product) and quoted against the discontinued med's Form, so the
  // denominator carries the dose form ("10 mg per 1 tablet") instead of a unitless
  // `{ value: 1 }`. Emitted so the BE's required min-1 `ingredient[]` still names
  // the swapped-out product.
  const ingredient = buildIngredientInput(medicationConcept, dosageStepToValue(step), step.doseUnit, medicationForm);
  // Carry the discontinued med's Form onto both steps so the swapped-out record
  // keeps its dose form. "Unknown"/no form leaves the concept text empty.
  const form = buildFormConcept(medicationForm);
  const current: TitrationStepInput = { ingredient, form, dosage };
  // The target dose read off the MedicationRequest extension is a strength, so its
  // value + unit land on the target `ingredient[].strength.numerator` (quoted
  // against the same Form as the current step), never on `doseAndRate` — "Strength
  // is not quantity". The remaining schedule (if any) stays on the target `dosage`.
  // With no target dose, the step still names the swapped-out product to satisfy
  // the BE's min-1 `ingredient[]`.
  const targetStrength = targetDoseStrength(targetDose);
  const targetSchedule = targetDose && stripDoseAndRate(targetDose);
  const target: TitrationStepInput = {
    ingredient: buildIngredientInput(
      medicationConcept,
      targetStrength?.value ?? {},
      targetStrength?.unit ?? "",
      medicationForm
    ),
    form,
    ...(targetSchedule && { dosage: [targetSchedule] }),
  };
  return {
    drug_class: FE_TO_BE_DRUG_CLASS[medClass],
    medication_codeable_concept: medicationConcept,
    current,
    target,
    // decision, decision_reasons, reason_for_titration, source_medication_statement_id
    ...replacedMedication,
  };
};

/**
 * Build the target step's *schedule* (frequency, timing, route) as a single
 * `DosageInput`. The target dose *value* is a strength ("40 mg") and rides on the
 * target `ingredient[].strength.numerator`, never on `doseAndRate` — "Strength is
 * not quantity" (MEDICATION_TRANSFORM_RULES.md). So this deliberately omits the
 * dose value; with no target value entered there is nothing to titrate toward, so
 * it returns `undefined`. Returns `undefined` too when the schedule is empty
 * (no frequency and no route), leaving the strength to stand on its own.
 */
const buildTargetSchedule = (entry: TitrationPlanEntry): DosageInput | undefined => {
  if (!dosageValueHasContent(entry.targetDosage.value)) {
    return undefined;
  }
  const step: DosageStep = {
    ...emptyDosageStep(),
    frequencyPreset: entry.targetDosage.frequency,
  };
  // stepsToDosage emits fhirR4.Dosage[]; the BE DosageInput shape is a
  // structural subset, so the cast is safe at the API boundary. The target is
  // single-step, so we take the first (and only) element.
  return (stepsToDosage([step], { route: entry.route.trim() || undefined }) as DosageInput[])[0];
};

/**
 * The target dose value + unit, read off a target `DosageInput`'s `doseAndRate`.
 * Used to relocate a discontinued med's stored target dose onto the target
 * `ingredient[].strength.numerator` rather than leaving it on `doseAndRate`.
 */
const targetDoseStrength = (dose?: DosageInput): { value: DosageValue; unit: string } | undefined => {
  const quantity = dose?.doseAndRate?.[0]?.doseQuantity;
  if (quantity?.value === undefined) {
    return undefined;
  }
  return { value: { low: quantity.value }, unit: quantity.unit ?? "" };
};

/**
 * Drop `doseAndRate` from a target `DosageInput`, keeping only the schedule
 * (timing, route, ...). Returns `undefined` when nothing but the dose value was
 * present, so an empty schedule is omitted rather than sent as `{}`.
 */
const stripDoseAndRate = (dose: DosageInput): DosageInput | undefined => {
  const { doseAndRate: _doseAndRate, ...schedule } = dose;
  return Object.keys(schedule).length ? schedule : undefined;
};

export const entryToMedicationInput = (
  medClass: GdmtMedicationClass,
  entry: TitrationPlanEntry
): TitrationMedicationInput => {
  const { decision, decisionReasons, reason } = entry;
  const medicationConcept: CodeableConceptInput = {
    text: entry.medicationName.trim(),
  };

  const step = entryToDosageStep(entry);
  // stepsToDosage emits fhirR4.Dosage[]; the BE DosageInput shape is a
  // structural subset, so the cast is safe at the API boundary.
  const dosage = stepsToDosage([step], { route: entry.route.trim() || undefined }) as DosageInput[];

  // Strength Ratio ("25 mg per 1 tablet") from the same dose that drives the
  // `dosage[]` — `newDosage` when the practitioner has entered a new dose, else
  // the current one (mirrors `entryToDosageStep`).
  const strength = dosageValueHasContent(entry.newDosage.value) ? entry.newDosage : entry.currentDosage;
  const ingredient = buildIngredientInput(medicationConcept, strength.value, strength.unit, entry.form);
  const form = buildFormConcept(entry.form);

  // The `current` step is the dose being started (the practitioner's new dose,
  // falling back to the patient's current one); the `target` step is the dose
  // being titrated toward, off `targetDosage`. Both quote their strength against
  // the same Form.
  const current: TitrationStepInput = { ingredient, form, dosage };

  // The target dose *value* is a strength, so it lives on the target ingredient's
  // `strength.numerator`; the target `dosage` carries only the schedule.
  const targetSchedule = buildTargetSchedule(entry);
  const target: TitrationStepInput = {
    ingredient: buildIngredientInput(medicationConcept, entry.targetDosage.value, entry.targetDosage.unit, entry.form),
    // Always carry the Form (from `entry.form`, empty text for a non-named form
    // in `buildFormConcept`) so `target.form` mirrors `current.form`.
    form,
    ...(targetSchedule && { dosage: [targetSchedule] }),
  };

  // When the medication was picked from the patient's existing MSes,
  // `medicationId` IS the source MedicationStatement id — pass it through so
  // the BE can link the new titration plan back to the originating record.
  const sourceMedicationStatementId =
    entry.medicationId && entry.medicationId !== CUSTOM_MEDICATION_ID ? entry.medicationId : undefined;

  return {
    drug_class: FE_TO_BE_DRUG_CLASS[medClass],
    medication_codeable_concept: medicationConcept,
    current,
    target,
    ...(entry.dateStarted?.trim() && { date_started: entry.dateStarted.trim() }),
    ...(sourceMedicationStatementId && { source_medication_statement_id: sourceMedicationStatementId }),
    ...(reason.trim() && { reason_for_titration: reason.trim() }),
    ...(decision && { decision }),
    ...(decisionReasons.length > 0 && { decision_reasons: decisionReasons }),
  };
};
